import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { FixedPriceOverviewDto } from '../../../config/registrationConfig'

import ViewRegistrationRow, {
    ViewRegistrationColumnProps,
    ViewRegistrationColumns,
    ViewRegistrationRowProps,
} from '../Registration/ViewRegistrationRow'
import MoneyText from '../../Text/Currency/MoneyText'
import { multiplyRate } from '../../../helpers/numberHelper'
import { Stack, StackItem, Text } from '@fluentui/react'
import { FixedPriceListProps } from './FixedPriceList'
import useVatType from '../../../hooks/useVatType'
import useFixedPriceType from '../../../hooks/useFixedPriceType'
import useFixedPriceTypes from '../../../hooks/useFixedPriceTypes'

type Props = ViewRegistrationRowProps<FixedPriceOverviewDto, FixedPriceListProps>
type ColumnProps = ViewRegistrationColumnProps<FixedPriceOverviewDto, FixedPriceListProps>

function Columns(props: ColumnProps): ReactElement {
    const { vatType } = useVatType(props.item.vatType)
    const [fixedPriceType] = useFixedPriceType(props.item.type)
    const { fixedPriceTypes } = useFixedPriceTypes()

    const useName = useMemo<boolean>(() => fixedPriceTypes.filter(type => !!type.longDescription).length === 0, [fixedPriceTypes])

    const vatValue = useMemo(() => vatType !== undefined && multiplyRate(vatType.percentage / 100, props.item.rate), [props.item])
    const totalValue = useMemo(() => vatType !== undefined && multiplyRate((vatType?.percentage || 0) / 100 + 1, props.item.rate), [props.item])

    switch (props.column.key) {
        case 'type':
            return fixedPriceType !== undefined && <Text variant="small">{useName ? fixedPriceType?.name : fixedPriceType?.description}</Text>
        case 'description':
            return <Text variant="small">{props.item.description}</Text>
        case 'rate':
            return (!fixedPriceType || fixedPriceType?.prefillType) && props.item.rate !== undefined && <MoneyText value={props.item.rate} />
        case 'vat':
            return (
                (!fixedPriceType || fixedPriceType?.prefillType) &&
                vatValue && (
                    <Stack horizontal tokens={{ childrenGap: 0 }}>
                        <StackItem grow>
                            <Text variant="small">
                                <MoneyText value={vatValue} align="left" />
                            </Text>
                        </StackItem>
                        <StackItem>
                            <Text variant="xSmall">{vatType.percentage}%</Text>
                        </StackItem>
                    </Stack>
                )
            )
        case 'total':
            return (!fixedPriceType || fixedPriceType?.prefillType) && totalValue && <MoneyText value={totalValue} align="right" />
    }

    return <ViewRegistrationColumns<FixedPriceOverviewDto, FixedPriceListProps> {...props} />
}

export default function ViewFixedPriceRow(props: Props): ReactElement {
    return <ViewRegistrationRow<FixedPriceOverviewDto, FixedPriceListProps> {...props} columns={Columns} />
}
